import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Plus, Search, Eye, Truck, ExternalLink, Car, Trash2, ChevronDown, ChevronUp, Calendar, Gauge, Shield, Wrench, Pencil, FileText, Upload, X, Check, Clock, Download, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { formatDate, cn } from '../lib/utils';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSearchHighlight, HighlightedText } from '../hooks/useSearchHighlight';
import { useSmartRefresh, WS_EVENTS } from '../hooks/useSmartRefresh';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

// Document categories for vehicles
const DOCUMENT_CATEGORIES = [
  { id: 'carte_grise', label: 'Carte grise', required: true },
  { id: 'controle_technique', label: 'Contrôle technique', required: true },
  { id: 'assurance', label: 'Assurance', required: true },
  { id: 'autre_1', label: 'Autre document 1', required: false },
  { id: 'autre_2', label: 'Autre document 2', required: false }
];

const MAX_DOCUMENTS = 5;

// Status configuration - aligned with backend enum
const STATUS_CONFIG = {
  ok: { color: 'bg-green-500', label: 'OK', filterColor: 'text-green-500', order: 1 },
  a_surveiller: { color: 'bg-yellow-500', label: 'À surveiller', filterColor: 'text-yellow-500', order: 2 },
  en_maintenance: { color: 'bg-orange-500', label: 'Maintenance', filterColor: 'text-orange-500', order: 3 },
  immobilise: { color: 'bg-red-500', label: 'Immobilisé', filterColor: 'text-red-500', order: 4 }
};

// Sort vehicles by status (green first, red last)
const sortByStatus = (vehicles) => {
  return [...vehicles].sort((a, b) => {
    const orderA = STATUS_CONFIG[a.statut]?.order || 99;
    const orderB = STATUS_CONFIG[b.statut]?.order || 99;
    return orderA - orderB;
  });
};

// Vehicle Card Component with floating dropdown
const VehicleCard = ({ vehicle, highlightTerm, onDelete, onView, onConsult, onStatusChange, hasAdminRole, theme, isExpanded, onToggle, isChangingStatus }) => {
  const statusConfig = STATUS_CONFIG[vehicle.statut] || STATUS_CONFIG.ok;

  return (
    <div className="relative" data-testid={`vehicle-card-${vehicle.id}`}>
      {/* Carte principale */}
      <button
        onClick={onToggle}
        className={cn(
          "w-full rounded-xl border transition-all flex items-center justify-center p-4 text-left relative",
          theme === 'dark' ? 'bg-zinc-900/50 border-white/10 hover:border-white/20' : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm',
          isExpanded && 'ring-2 ring-red-500/50'
        )}
      >
        <div className="flex items-center justify-center gap-3">
          <div className={cn("w-3 h-3 rounded-full flex-shrink-0", statusConfig.color)} title={statusConfig.label} />
          <span className={cn("font-bold text-lg tracking-wide text-center", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
            <HighlightedText text={vehicle.immatriculation} searchTerm={highlightTerm} />
          </span>
        </div>
        <div className="absolute right-4">
          {isExpanded ? (
            <ChevronUp size={18} className={theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'} />
          ) : (
            <ChevronDown size={18} className={theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'} />
          )}
        </div>
      </button>

      {/* Menu déroulant flottant */}
      {isExpanded && (
        <div 
          className={cn(
            "absolute left-0 right-0 top-full mt-1 z-50 rounded-xl border shadow-2xl overflow-hidden",
            theme === 'dark' ? 'bg-zinc-900 border-white/10' : 'bg-white border-gray-200'
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4">
            {/* Informations en grille */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <Truck size={14} className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'} />
                <span className={cn("text-sm", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>
                  <HighlightedText text={`${vehicle.marque} ${vehicle.modele}`} searchTerm={highlightTerm} />
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={14} className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'} />
                <span className={cn("text-sm", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>
                  {vehicle.annee}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Gauge size={14} className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'} />
                <span className={cn("text-sm", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>
                  {vehicle.kilometrage?.toLocaleString() || 0} km
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className={cn("w-2 h-2 rounded-full", statusConfig.color)} />
                <span className={cn("text-sm font-medium", statusConfig.filterColor)}>
                  {statusConfig.label}
                </span>
              </div>
            </div>

            {/* Dates importantes */}
            {(vehicle.date_assurance || vehicle.date_controle_technique) && (
              <div className={cn(
                "flex flex-wrap gap-4 mt-3 pt-3 border-t text-sm",
                theme === 'dark' ? 'border-white/10 text-zinc-400' : 'border-gray-100 text-gray-500'
              )}>
                {vehicle.date_assurance && (
                  <div className="flex items-center gap-1">
                    <Shield size={12} />
                    <span>Assurance: <strong className={theme === 'dark' ? 'text-zinc-200' : 'text-gray-700'}>{formatDate(vehicle.date_assurance)}</strong></span>
                  </div>
                )}
                {vehicle.date_controle_technique && (
                  <div className="flex items-center gap-1">
                    <Wrench size={12} />
                    <span>CT: <strong className={theme === 'dark' ? 'text-zinc-200' : 'text-gray-700'}>{formatDate(vehicle.date_controle_technique)}</strong></span>
                  </div>
                )}
              </div>
            )}

            {/* Changement de statut */}
            {hasAdminRole && (
              <div className={cn(
                "mt-3 pt-3 border-t",
                theme === 'dark' ? 'border-white/10' : 'border-gray-100'
              )}>
                <p className={cn("text-xs font-medium mb-2", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
                  Changer le statut:
                </p>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(STATUS_CONFIG).map(([status, config]) => {
                    const isActive = vehicle.statut === status;
                    return (
                      <button
                        key={status}
                        disabled={isChangingStatus}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isActive && !isChangingStatus) onStatusChange(vehicle.id, status);
                        }}
                        className={cn(
                          "px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5",
                          isActive
                            ? `${config.color} text-white`
                            : theme === 'dark'
                              ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                          isChangingStatus && 'opacity-50 cursor-wait'
                        )}
                      >
                        <div className={cn("w-2 h-2 rounded-full", isActive ? 'bg-white' : config.color)} />
                        {config.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className={cn(
              "flex gap-2 mt-3 pt-3 border-t",
              theme === 'dark' ? 'border-white/10' : 'border-gray-100'
            )}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onConsult(vehicle)}
                className={cn(
                  "flex-1",
                  theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white hover:bg-white/10' : 'border-gray-200 hover:bg-gray-100'
                )}
                data-testid={`consult-vehicle-${vehicle.id}`}
              >
                <Eye size={16} className="mr-1" />
                Consulter
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onView(vehicle)}
                className={cn(
                  "flex-1",
                  theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white hover:bg-white/10' : 'border-gray-200 hover:bg-gray-100'
                )}
                data-testid={`view-vehicle-${vehicle.id}`}
              >
                <Pencil size={16} className="mr-1" />
                Modifier
              </Button>
              {hasAdminRole && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDelete(vehicle.id, vehicle.immatriculation)}
                  className="bg-red-500/10 border-red-500/30 text-red-500 hover:bg-red-500/20"
                  data-testid={`delete-vehicle-${vehicle.id}`}
                >
                  <Trash2 size={16} />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('vehicles');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [changingStatusId, setChangingStatusId] = useState(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const { theme } = useTheme();
  const { searchTerm: highlightTerm } = useSearchHighlight();
  
  // State for status change dialog
  const [statusChangeDialog, setStatusChangeDialog] = useState({
    isOpen: false,
    vehicleId: null,
    vehicleName: '',
    currentStatus: '',
    newStatus: '',
    note: ''
  });
  const [isSubmittingStatus, setIsSubmittingStatus] = useState(false);

  // State for vehicle edit modal
  const [editModal, setEditModal] = useState({
    isOpen: false,
    vehicle: null
  });
  const [editFormData, setEditFormData] = useState({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  
  // State for document preview modal
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    document: null,
    loading: true
  });

  // State for consult modal (view only, no edit)
  const [consultModal, setConsultModal] = useState({
    isOpen: false,
    vehicle: null
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setExpandedId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchVehicles();
  }, []);

  useEffect(() => {
    let filtered = vehicles;
    
    if (searchTerm) {
      filtered = filtered.filter(v =>
        `${v.immatriculation} ${v.marque} ${v.modele}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(v => v.statut === statusFilter);
    }
    
    // Sort by status (green first, red last)
    filtered = sortByStatus(filtered);
    
    setFilteredVehicles(filtered);
  }, [searchTerm, vehicles, statusFilter]);

  const fetchVehicles = useCallback(async () => {
    try {
      const response = await api.get('/vehicles');
      // Sort by status on initial load
      setVehicles(sortByStatus(response.data));
      setFilteredVehicles(sortByStatus(response.data));
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Erreur lors du chargement de la flotte');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  // Smart refresh: WebSocket principal, polling fallback 30s
  // Désactivé quand un dialog est ouvert
  useSmartRefresh(fetchVehicles, {
    wsEvents: [WS_EVENTS.VEHICLE_UPDATED, WS_EVENTS.VEHICLE_CREATED, WS_EVENTS.VEHICLE_DELETED],
    fallbackInterval: 30000,
    enabled: !statusChangeDialog.isOpen && !isSubmittingStatus && !expandedId && !editModal.isOpen
  });

  const handleDeleteVehicle = async (vehicleId, immatriculation) => {
    if (!window.confirm(`Supprimer le véhicule ${immatriculation} ?`)) return;
    try {
      await api.delete(`/vehicles/${vehicleId}`);
      toast.success('Véhicule supprimé');
      setExpandedId(null);
      fetchVehicles();
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Open edit modal with vehicle data
  const handleOpenEditModal = (vehicle) => {
    setEditFormData({
      marque: vehicle.marque || '',
      modele: vehicle.modele || '',
      annee: vehicle.annee || new Date().getFullYear(),
      kilometrage: vehicle.kilometrage || 0,
      carburant: vehicle.carburant || 'diesel',
      date_assurance: vehicle.date_assurance || '',
      date_controle_technique: vehicle.date_controle_technique || '',
      documents: vehicle.documents || [],
      field_updates: vehicle.field_updates || {}
    });
    setEditModal({
      isOpen: true,
      vehicle: vehicle
    });
  };

  // Open consult modal (view only)
  const handleOpenConsultModal = (vehicle) => {
    setConsultModal({
      isOpen: true,
      vehicle: vehicle
    });
  };

  // Save edited vehicle
  const handleSaveEdit = async () => {
    if (!editModal.vehicle) return;
    
    setIsSavingEdit(true);
    try {
      // Prepare update data with field timestamps
      const now = new Date().toISOString();
      const updateData = {
        marque: editFormData.marque,
        modele: editFormData.modele,
        annee: parseInt(editFormData.annee),
        kilometrage: parseInt(editFormData.kilometrage),
        carburant: editFormData.carburant,
        date_assurance: editFormData.date_assurance,
        date_controle_technique: editFormData.date_controle_technique,
        documents: editFormData.documents,
        field_updates: {
          ...editFormData.field_updates
        }
      };

      // Track which fields changed
      const originalVehicle = editModal.vehicle;
      if (editFormData.marque !== originalVehicle.marque) updateData.field_updates.marque = now;
      if (editFormData.modele !== originalVehicle.modele) updateData.field_updates.modele = now;
      if (editFormData.annee !== originalVehicle.annee) updateData.field_updates.annee = now;
      if (editFormData.kilometrage !== originalVehicle.kilometrage) updateData.field_updates.kilometrage = now;
      if (editFormData.carburant !== originalVehicle.carburant) updateData.field_updates.carburant = now;
      if (editFormData.date_assurance !== originalVehicle.date_assurance) updateData.field_updates.date_assurance = now;
      if (editFormData.date_controle_technique !== originalVehicle.date_controle_technique) updateData.field_updates.date_controle_technique = now;

      await api.put(`/vehicles/${editModal.vehicle.id}`, updateData);
      toast.success('Véhicule mis à jour');
      setEditModal({ isOpen: false, vehicle: null });
      fetchVehicles();
    } catch (error) {
      console.error('Error updating vehicle:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Handle document upload - Upload to backend API
  const handleDocumentUpload = async (categoryId, file) => {
    if (!file) return;
    
    // Check max documents
    const currentDocs = editFormData.documents || [];
    if (currentDocs.length >= MAX_DOCUMENTS && !currentDocs.find(d => d.category === categoryId)) {
      toast.error(`Maximum ${MAX_DOCUMENTS} documents par véhicule`);
      return;
    }
    
    // Check file size (10 MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 MB)');
      return;
    }
    
    // Check file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Acceptés: PDF, JPG, PNG');
      return;
    }
    
    setUploadingDoc(categoryId);
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', categoryId);
      
      // Upload to backend
      const response = await api.post(`/vehicles/${editModal.vehicle.id}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        // Refresh vehicle data to get updated documents
        const vehicleResponse = await api.get(`/vehicles/${editModal.vehicle.id}`);
        const updatedVehicle = vehicleResponse.data;
        
        setEditFormData(prev => ({
          ...prev,
          documents: updatedVehicle.documents || [],
          field_updates: updatedVehicle.field_updates || {}
        }));
        
        toast.success(`Document ${DOCUMENT_CATEGORIES.find(c => c.id === categoryId)?.label} téléversé`);
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      const message = error.response?.data?.detail || 'Erreur lors du téléversement';
      toast.error(message);
    } finally {
      setUploadingDoc(null);
    }
  };

  // Remove document - Call backend API
  const handleRemoveDocument = async (categoryId) => {
    if (!editModal.vehicle) return;
    
    try {
      await api.delete(`/vehicles/${editModal.vehicle.id}/documents/category/${categoryId}`);
      
      // Refresh vehicle data
      const vehicleResponse = await api.get(`/vehicles/${editModal.vehicle.id}`);
      const updatedVehicle = vehicleResponse.data;
      
      setEditFormData(prev => ({
        ...prev,
        documents: updatedVehicle.documents || []
      }));
      
      toast.success('Document supprimé');
    } catch (error) {
      console.error('Error deleting document:', error);
      const message = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(message);
    }
  };

  // Open document preview modal
  const handlePreviewDocument = (doc) => {
    setPreviewModal({
      isOpen: true,
      document: doc,
      loading: true
    });
  };

  // Format update date
  const formatUpdateDate = (isoDate) => {
    if (!isoDate) return null;
    const date = new Date(isoDate);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const handleStatusChange = async (vehicleId, newStatus) => {
    // Get current vehicle data
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (!vehicle) return;
    
    // If same status, do nothing
    if (vehicle.statut === newStatus) return;
    
    // Open dialog to ask for note
    setStatusChangeDialog({
      isOpen: true,
      vehicleId: vehicleId,
      vehicleName: `${vehicle.marque} ${vehicle.modele} (${vehicle.immatriculation})`,
      currentStatus: vehicle.statut,
      newStatus: newStatus,
      note: ''
    });
  };
  
  const handleConfirmStatusChange = async () => {
    const { vehicleId, newStatus, note } = statusChangeDialog;
    
    if (!note.trim()) {
      toast.error('Veuillez saisir une note pour expliquer ce changement de statut');
      return;
    }
    
    setIsSubmittingStatus(true);
    setChangingStatusId(vehicleId);
    
    try {
      const response = await api.put(`/vehicles/${vehicleId}/status`, { 
        statut: newStatus,
        note: note.trim()
      });
      
      // Close dialog FIRST
      setStatusChangeDialog({
        isOpen: false,
        vehicleId: null,
        vehicleName: '',
        currentStatus: '',
        newStatus: '',
        note: ''
      });
      
      toast.success(`Statut changé en "${STATUS_CONFIG[newStatus]?.label}"`);
      
      // Update local state
      setVehicles(prev => sortByStatus(prev.map(v => 
        v.id === vehicleId ? { ...v, statut: newStatus } : v
      )));
      
    } catch (error) {
      console.error('Error updating status:', error);
      const message = error.response?.data?.detail || 'Erreur lors de la mise à jour du statut';
      toast.error(message);
    } finally {
      setIsSubmittingStatus(false);
      setChangingStatusId(null);
    }
  };

  const handleToggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const countByStatus = (status) => vehicles.filter(v => v.statut === status).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-zinc-800 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-1 md:p-0 pb-24 md:pb-0" data-testid="vehicles-page" ref={containerRef}>
      {/* Header Row - Compact on mobile */}
      <div className="flex items-center justify-center gap-2">
        {/* Tabs */}
        <div className={cn(
          "flex items-center gap-1 rounded-lg p-1 border shrink-0",
          theme === 'dark' ? 'bg-zinc-800/50 border-white/5' : 'bg-gray-100 border-gray-200'
        )}>
          <button
            onClick={() => setActiveTab('vehicles')}
            className={cn(
              "px-2.5 py-1.5 rounded text-xs font-medium transition-all flex items-center gap-1",
              activeTab === 'vehicles' 
                ? 'bg-red-500 text-white shadow' 
                : theme === 'dark' ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:bg-white'
            )}
            data-testid="tab-vehicles"
          >
            <Truck size={14} />
            <span className="hidden sm:inline">Véhicules</span>
          </button>
          <button
            onClick={() => setActiveTab('pave')}
            className={cn(
              "px-2.5 py-1.5 rounded text-xs font-medium transition-all flex items-center gap-1",
              activeTab === 'pave' 
                ? 'bg-cyan-500 text-white shadow' 
                : theme === 'dark' ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:bg-white'
            )}
            data-testid="tab-pave"
          >
            <Car size={14} />
            <span className="hidden sm:inline">PAVE</span>
          </button>
        </div>

        {activeTab === 'vehicles' && (
          <>
            {/* Status Filter Dropdown */}
            <div className="relative shrink-0">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={cn(
                  "h-9 px-2 pr-7 rounded-lg border text-xs font-medium appearance-none cursor-pointer",
                  theme === 'dark'
                    ? 'bg-zinc-800/50 border-white/10 text-white'
                    : 'bg-white border-gray-200 text-gray-700'
                )}
                data-testid="status-filter"
              >
                <option value="all">Tous ({vehicles.length})</option>
                {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                  <option key={status} value={status}>
                    {config.label} ({countByStatus(status)})
                  </option>
                ))}
              </select>
              <ChevronDown size={12} className={cn(
                "absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none",
                theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'
              )} />
            </div>

            {hasRole(['admin', 'responsable']) && (
              <Button 
                variant="brand"
                onClick={() => navigate('/vehicles/new')} 
                data-testid="add-vehicle-button"
                className="h-9 w-9 p-0 shrink-0"
                title="Ajouter"
              >
                <Plus size={16} />
              </Button>
            )}
          </>
        )}
      </div>

      {/* Search Bar - Centered below buttons */}
      {activeTab === 'vehicles' && (
        <div className="flex justify-center mt-2">
          <div className="relative w-44">
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-zinc-500" size={14} />
            <Input
              type="text"
              placeholder="AA-123-BB"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={cn(
                "pl-8 h-9 text-sm text-center",
                theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white placeholder:text-zinc-500' : 'bg-white border-gray-200'
              )}
              data-testid="search-input"
            />
          </div>
        </div>
      )}

      {/* Vehicles Tab Content */}
      {activeTab === 'vehicles' && (
        <div>
          {/* Vehicle Count */}
          <p className={cn("text-xs mb-4 text-center", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>
            {filteredVehicles.length} véhicule{filteredVehicles.length > 1 ? 's' : ''}
          </p>

          {/* Vehicles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 max-w-5xl mx-auto">
            {filteredVehicles.map((vehicle) => (
              <VehicleCard
                key={vehicle.id}
                vehicle={vehicle}
                highlightTerm={highlightTerm}
                onDelete={handleDeleteVehicle}
                onView={handleOpenEditModal}
                onConsult={handleOpenConsultModal}
                onStatusChange={handleStatusChange}
                hasAdminRole={hasRole(['admin', 'responsable'])}
                theme={theme}
                isExpanded={expandedId === vehicle.id}
                onToggle={() => handleToggleExpand(vehicle.id)}
                isChangingStatus={changingStatusId === vehicle.id}
              />
            ))}
          </div>

          {filteredVehicles.length === 0 && (
            <div className={cn(
              "text-center py-12 rounded-xl border w-full max-w-5xl",
              theme === 'dark' ? 'bg-zinc-900/50 border-white/10 text-zinc-500' : 'bg-white border-gray-200 text-gray-500'
            )}>
              Aucun véhicule trouvé
            </div>
          )}
        </div>
      )}

      {/* PAVE Tab Content */}
      {activeTab === 'pave' && (
        <div className="space-y-6 max-w-5xl mx-auto">
          <div className={cn(
            "rounded-xl p-6 border",
            theme === 'dark' ? 'bg-cyan-500/5 border-cyan-500/20' : 'bg-cyan-50 border-cyan-200'
          )}>
            <div className="flex items-start gap-4">
              <div className={cn(
                "w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0",
                theme === 'dark' ? 'bg-cyan-500/20' : 'bg-cyan-100'
              )}>
                <Car size={28} className="text-cyan-500" />
              </div>
              <div className="flex-1">
                <h2 className={cn("text-xl font-bold uppercase tracking-wide mb-2", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                  PAVE Fleet Dashboard
                </h2>
                <p className={cn("text-sm mb-4", theme === 'dark' ? 'text-zinc-400' : 'text-gray-600')}>
                  Gestion avancée de votre flotte : suivi temps réel, diagnostics et maintenance prédictive.
                </p>
                <a
                  href="https://dashboard.paveapi.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-500 text-white font-bold uppercase tracking-wider rounded-md shadow-[0_0_15px_rgba(239,68,68,0.4)] hover:shadow-[0_0_25px_rgba(239,68,68,0.5)] hover:bg-red-600 transition-all"
                >
                  <ExternalLink size={18} />
                  Ouvrir PAVE Dashboard
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Status Change Dialog */}
      <Dialog open={statusChangeDialog.isOpen} onOpenChange={(open) => {
        if (!open && !isSubmittingStatus) {
          setStatusChangeDialog(prev => ({ ...prev, isOpen: false }));
        }
      }}>
        <DialogContent className={cn(
          "max-w-md",
          theme === 'dark' ? 'bg-zinc-900 border-white/10' : 'bg-white'
        )}>
          <DialogHeader>
            <DialogTitle className={cn(theme === 'dark' ? 'text-white' : 'text-gray-900')}>
              Changement de statut
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <p className={cn("text-sm font-medium mb-1", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>
                Véhicule
              </p>
              <p className={cn("text-sm", theme === 'dark' ? 'text-zinc-400' : 'text-gray-600')}>
                {statusChangeDialog.vehicleName}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className={cn("w-3 h-3 rounded-full", STATUS_CONFIG[statusChangeDialog.currentStatus]?.color || 'bg-gray-400')} />
                <span className={cn("text-sm", theme === 'dark' ? 'text-zinc-400' : 'text-gray-600')}>
                  {STATUS_CONFIG[statusChangeDialog.currentStatus]?.label || statusChangeDialog.currentStatus}
                </span>
              </div>
              <span className={cn("text-sm", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>→</span>
              <div className="flex items-center gap-2">
                <div className={cn("w-3 h-3 rounded-full", STATUS_CONFIG[statusChangeDialog.newStatus]?.color || 'bg-gray-400')} />
                <span className={cn("text-sm font-medium", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                  {STATUS_CONFIG[statusChangeDialog.newStatus]?.label || statusChangeDialog.newStatus}
                </span>
              </div>
            </div>
            
            <div>
              <label className={cn("text-sm font-medium mb-2 block", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>
                Note obligatoire <span className="text-red-500">*</span>
              </label>
              <Textarea
                placeholder="Expliquez la raison de ce changement de statut..."
                value={statusChangeDialog.note}
                onChange={(e) => setStatusChangeDialog(prev => ({ ...prev, note: e.target.value }))}
                className={cn(
                  "min-h-[100px] resize-none",
                  theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white placeholder:text-zinc-500' : ''
                )}
                disabled={isSubmittingStatus}
              />
              <p className={cn("text-xs mt-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>
                Cette note sera conservée dans l'historique du véhicule
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setStatusChangeDialog(prev => ({ ...prev, isOpen: false }))}
              disabled={isSubmittingStatus}
              className={theme === 'dark' ? 'border-white/10 hover:bg-zinc-800' : ''}
            >
              Annuler
            </Button>
            <Button
              onClick={handleConfirmStatusChange}
              disabled={isSubmittingStatus || !statusChangeDialog.note.trim()}
              className={cn(
                "text-white",
                STATUS_CONFIG[statusChangeDialog.newStatus]?.color || 'bg-blue-500'
              )}
            >
              {isSubmittingStatus ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                'Confirmer le changement'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vehicle Edit Modal */}
      <Dialog open={editModal.isOpen} onOpenChange={(open) => !isSavingEdit && setEditModal({ isOpen: open, vehicle: open ? editModal.vehicle : null })}>
        <DialogContent className={cn(
          "max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl",
          theme === 'dark' ? 'bg-zinc-900 border-white/10' : 'bg-white border-gray-200'
        )}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
              <Truck className="text-red-500" size={20} />
              {editModal.vehicle?.immatriculation}
              <span className={cn("text-sm font-normal", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
                {editModal.vehicle?.marque} {editModal.vehicle?.modele}
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Vehicle Information */}
            <div>
              <h3 className={cn("text-sm font-semibold mb-3 flex items-center gap-2", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                <Car size={16} className="text-red-500" />
                Informations du véhicule
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Immatriculation - Read Only */}
                <div className="col-span-2">
                  <Label className={cn("text-xs", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
                    Immatriculation (non modifiable)
                  </Label>
                  <Input 
                    value={editModal.vehicle?.immatriculation || ''} 
                    disabled 
                    className={cn(
                      "font-mono font-bold rounded-lg",
                      theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-zinc-400' : 'bg-gray-100 border-gray-200 text-gray-500'
                    )}
                  />
                </div>
                
                {/* Marque */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Marque</Label>
                    {editFormData.field_updates?.marque && (
                      <span className={cn("text-[10px] flex items-center gap-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                        <Clock size={10} /> {formatUpdateDate(editFormData.field_updates.marque)}
                      </span>
                    )}
                  </div>
                  <Input 
                    value={editFormData.marque || ''} 
                    onChange={(e) => setEditFormData(prev => ({ ...prev, marque: e.target.value }))}
                    className={cn(
                      "rounded-lg",
                      theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-gray-200'
                    )}
                  />
                </div>
                
                {/* Modèle */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Modèle</Label>
                    {editFormData.field_updates?.modele && (
                      <span className={cn("text-[10px] flex items-center gap-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                        <Clock size={10} /> {formatUpdateDate(editFormData.field_updates.modele)}
                      </span>
                    )}
                  </div>
                  <Input 
                    value={editFormData.modele || ''} 
                    onChange={(e) => setEditFormData(prev => ({ ...prev, modele: e.target.value }))}
                    className={cn(
                      "rounded-lg",
                      theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-gray-200'
                    )}
                  />
                </div>
                
                {/* Année */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Année</Label>
                    {editFormData.field_updates?.annee && (
                      <span className={cn("text-[10px] flex items-center gap-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                        <Clock size={10} /> {formatUpdateDate(editFormData.field_updates.annee)}
                      </span>
                    )}
                  </div>
                  <Input 
                    type="number"
                    value={editFormData.annee || ''} 
                    onChange={(e) => setEditFormData(prev => ({ ...prev, annee: e.target.value }))}
                    className={cn(
                      "rounded-lg",
                      theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-gray-200'
                    )}
                  />
                </div>
                
                {/* Kilométrage */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Kilométrage</Label>
                    {editFormData.field_updates?.kilometrage && (
                      <span className={cn("text-[10px] flex items-center gap-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                        <Clock size={10} /> {formatUpdateDate(editFormData.field_updates.kilometrage)}
                      </span>
                    )}
                  </div>
                  <Input 
                    type="number"
                    value={editFormData.kilometrage || ''} 
                    onChange={(e) => setEditFormData(prev => ({ ...prev, kilometrage: e.target.value }))}
                    className={cn(
                      "rounded-lg",
                      theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-gray-200'
                    )}
                  />
                </div>
                
                {/* Carburant */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Carburant</Label>
                    {editFormData.field_updates?.carburant && (
                      <span className={cn("text-[10px] flex items-center gap-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                        <Clock size={10} /> {formatUpdateDate(editFormData.field_updates.carburant)}
                      </span>
                    )}
                  </div>
                  <select 
                    value={editFormData.carburant || 'diesel'} 
                    onChange={(e) => setEditFormData(prev => ({ ...prev, carburant: e.target.value }))}
                    className={cn(
                      "w-full h-10 px-3 rounded-lg border text-sm",
                      theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'
                    )}
                  >
                    <option value="diesel">Diesel</option>
                    <option value="essence">Essence</option>
                    <option value="electrique">Électrique</option>
                    <option value="hybride">Hybride</option>
                    <option value="gpl">GPL</option>
                  </select>
                </div>
                
                {/* Date Assurance */}
                <div className="flex flex-col items-center">
                  <div className="flex items-center justify-center gap-2">
                    <Label className={cn('text-center', theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>Date Assurance</Label>
                    {editFormData.field_updates?.date_assurance && (
                      <span className={cn("text-[10px] flex items-center gap-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                        <Clock size={10} /> {formatUpdateDate(editFormData.field_updates.date_assurance)}
                      </span>
                    )}
                  </div>
                  <Input 
                    type="date"
                    value={editFormData.date_assurance || ''} 
                    onChange={(e) => setEditFormData(prev => ({ ...prev, date_assurance: e.target.value }))}
                    className={cn(
                      "rounded-lg h-10 mt-1",
                      theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white [&::-webkit-calendar-picker-indicator]:invert' : 'bg-white border-gray-200'
                    )}
                  />
                </div>
                
                {/* Date Contrôle Technique */}
                <div className="flex flex-col items-center">
                  <div className="flex items-center justify-center gap-2">
                    <Label className={cn('text-center', theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>Contrôle Technique</Label>
                    {editFormData.field_updates?.date_controle_technique && (
                      <span className={cn("text-[10px] flex items-center gap-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                        <Clock size={10} /> {formatUpdateDate(editFormData.field_updates.date_controle_technique)}
                      </span>
                    )}
                  </div>
                  <Input 
                    type="date"
                    value={editFormData.date_controle_technique || ''} 
                    onChange={(e) => setEditFormData(prev => ({ ...prev, date_controle_technique: e.target.value }))}
                    className={cn(
                      "rounded-lg h-10 mt-1",
                      theme === 'dark' ? 'bg-zinc-800 border-white/10 text-white [&::-webkit-calendar-picker-indicator]:invert' : 'bg-white border-gray-200'
                    )}
                  />
                </div>
              </div>
            </div>
            
            {/* Documents Section */}
            <div>
              <h3 className={cn("text-sm font-semibold mb-3 flex items-center gap-2", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                <FileText size={16} className="text-red-500" />
                Documents
                <span className={cn("text-xs font-normal", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                  ({(editFormData.documents || []).length}/{MAX_DOCUMENTS} max)
                </span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {DOCUMENT_CATEGORIES.map((category) => {
                  const doc = (editFormData.documents || []).find(d => d.category === category.id);
                  const isUploading = uploadingDoc === category.id;
                  
                  return (
                    <div 
                      key={category.id}
                      className={cn(
                        "p-3 rounded-lg border",
                        doc 
                          ? theme === 'dark' ? 'bg-green-500/10 border-green-500/30' : 'bg-green-50 border-green-200'
                          : theme === 'dark' ? 'bg-zinc-800/50 border-white/10' : 'bg-gray-50 border-gray-200'
                      )}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={cn(
                          "text-sm font-medium",
                          doc ? 'text-green-500' : theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'
                        )}>
                          {category.label}
                          {category.required && <span className="text-red-500 ml-1">*</span>}
                        </span>
                        {doc && (
                          <button
                            onClick={() => handleRemoveDocument(category.id)}
                            className="p-1 rounded hover:bg-red-500/20 text-red-500"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                      
                      {doc ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Check size={14} className="text-green-500" />
                            <span className={cn("text-xs truncate flex-1", theme === 'dark' ? 'text-zinc-400' : 'text-gray-600')}>
                              {doc.filename}
                            </span>
                            <button
                              onClick={() => handlePreviewDocument(doc)}
                              className={cn(
                                "p-1 rounded text-purple-500",
                                theme === 'dark' ? 'hover:bg-purple-500/20' : 'hover:bg-purple-100'
                              )}
                              title="Prévisualiser"
                            >
                              <Eye size={14} />
                            </button>
                            <button
                              onClick={() => {
                                // Download document
                                const link = document.createElement('a');
                                link.href = `${process.env.REACT_APP_BACKEND_URL}/api/vehicles/${editModal.vehicle.id}/documents/${doc.id}/download`;
                                link.target = '_blank';
                                link.click();
                              }}
                              className={cn(
                                "p-1 rounded text-blue-500",
                                theme === 'dark' ? 'hover:bg-blue-500/20' : 'hover:bg-blue-100'
                              )}
                              title="Télécharger"
                            >
                              <Download size={14} />
                            </button>
                          </div>
                          <div className={cn("text-[10px] flex items-center justify-between", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                            <span className="flex items-center gap-1">
                              <Clock size={10} />
                              {formatUpdateDate(doc.uploaded_at)}
                            </span>
                            <span>{(doc.size / 1024).toFixed(1)} KB</span>
                          </div>
                        </div>
                      ) : (
                        <label className={cn(
                          "flex items-center justify-center gap-2 p-2 border-2 border-dashed rounded cursor-pointer transition-all",
                          isUploading 
                            ? 'opacity-50 cursor-wait'
                            : theme === 'dark' 
                              ? 'border-white/10 hover:border-white/30 text-zinc-400 hover:text-white' 
                              : 'border-gray-300 hover:border-gray-400 text-gray-500 hover:text-gray-700'
                        )}>
                          {isUploading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                          ) : (
                            <Upload size={16} />
                          )}
                          <span className="text-xs">
                            {isUploading ? 'Téléchargement...' : 'Ajouter'}
                          </span>
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleDocumentUpload(category.id, e.target.files[0])}
                            disabled={isUploading || (editFormData.documents || []).length >= MAX_DOCUMENTS}
                          />
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
              
              <p className={cn("text-xs mt-2", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                Formats acceptés: PDF, JPG, PNG. Maximum 5 documents par véhicule.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setEditModal({ isOpen: false, vehicle: null })}
              disabled={isSavingEdit}
              className={theme === 'dark' ? 'border-white/10' : ''}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={isSavingEdit}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {isSavingEdit ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Check size={16} className="mr-2" />
              )}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Modal */}
      <Dialog open={previewModal.isOpen} onOpenChange={(open) => !open && setPreviewModal({ isOpen: false, document: null, loading: true })}>
        <DialogContent className={cn(
          "max-w-4xl max-h-[90vh] p-0 overflow-hidden rounded-xl",
          theme === 'dark' ? 'bg-zinc-900 border-white/10' : 'bg-white border-gray-200'
        )}>
          <DialogHeader className={cn(
            "p-4 pb-2 border-b",
            theme === 'dark' ? 'border-white/10' : 'border-gray-200'
          )}>
            <DialogTitle className={cn("flex items-center gap-2 text-sm", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
              <FileText className="text-purple-500" size={18} />
              {previewModal.document?.filename}
              <span className={cn("text-xs font-normal ml-2", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                {DOCUMENT_CATEGORIES.find(c => c.id === previewModal.document?.category)?.label}
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <div className={cn(
            "relative w-full h-[70vh] flex items-center justify-center",
            theme === 'dark' ? 'bg-zinc-950' : 'bg-gray-100'
          )}>
            {previewModal.loading && (
              <div className={cn(
                "absolute inset-0 flex items-center justify-center z-10",
                theme === 'dark' ? 'bg-zinc-950/80' : 'bg-gray-100/80'
              )}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
              </div>
            )}
            
            {previewModal.document && (
              <>
                {/* Image Preview */}
                {previewModal.document.type?.startsWith('image/') && (
                  <img
                    src={previewModal.document.data}
                    alt={previewModal.document.filename}
                    className="max-w-full max-h-full object-contain"
                    onLoad={() => setPreviewModal(prev => ({ ...prev, loading: false }))}
                    onError={() => {
                      setPreviewModal(prev => ({ ...prev, loading: false }));
                      toast.error('Erreur lors du chargement de l\'image');
                    }}
                  />
                )}
                
                {/* PDF Preview */}
                {previewModal.document.type === 'application/pdf' && (
                  <iframe
                    src={previewModal.document.data}
                    title={previewModal.document.filename}
                    className="w-full h-full border-0"
                    onLoad={() => setPreviewModal(prev => ({ ...prev, loading: false }))}
                  />
                )}
                
                {/* Unsupported format message */}
                {!previewModal.document.type?.startsWith('image/') && previewModal.document.type !== 'application/pdf' && (
                  <div className="text-center p-8">
                    <FileText size={48} className={theme === 'dark' ? 'text-zinc-600' : 'text-gray-400'} />
                    <p className={cn("text-sm mt-4", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
                      Prévisualisation non disponible pour ce type de fichier
                    </p>
                    <p className={cn("text-xs mt-2", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                      Type: {previewModal.document.type}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          
          <div className={cn(
            "flex items-center justify-between p-3 border-t",
            theme === 'dark' ? 'border-white/10 bg-zinc-900' : 'border-gray-200 bg-gray-50'
          )}>
            <div className={cn("text-xs", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
              {previewModal.document && (
                <>
                  <span>{(previewModal.document.size / 1024).toFixed(1)} KB</span>
                  <span className="mx-2">•</span>
                  <span>Ajouté le {formatUpdateDate(previewModal.document.uploaded_at)}</span>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const vehicleId = editModal.vehicle?.id || consultModal.vehicle?.id;
                  if (vehicleId && previewModal.document) {
                    const link = document.createElement('a');
                    link.href = `${process.env.REACT_APP_BACKEND_URL}/api/vehicles/${vehicleId}/documents/${previewModal.document.id}/download`;
                    link.target = '_blank';
                    link.click();
                  }
                }}
                className={cn(
                  "text-blue-500",
                  theme === 'dark' ? 'border-white/10 hover:bg-blue-500/10' : 'hover:bg-blue-50'
                )}
              >
                <Download size={14} className="mr-1" />
                Télécharger
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreviewModal({ isOpen: false, document: null, loading: true })}
                className={theme === 'dark' ? 'border-white/10' : ''}
              >
                Fermer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Consult Modal (View Only - No Edit) */}
      <Dialog open={consultModal.isOpen} onOpenChange={(open) => setConsultModal({ isOpen: open, vehicle: open ? consultModal.vehicle : null })}>
        <DialogContent className={cn(
          "max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl",
          theme === 'dark' ? 'bg-zinc-900 border-white/10' : 'bg-white border-gray-200'
        )}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
              <Eye className="text-blue-500" size={20} />
              {consultModal.vehicle?.immatriculation}
              <span className={cn("text-sm font-normal", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
                {consultModal.vehicle?.marque} {consultModal.vehicle?.modele}
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Vehicle Information - Read Only */}
            <div>
              <h3 className={cn("text-sm font-semibold mb-3 flex items-center gap-2", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                <Car size={16} className="text-blue-500" />
                Informations du véhicule
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className={cn(
                  "p-3 rounded-lg",
                  theme === 'dark' ? 'bg-zinc-800/50' : 'bg-gray-50'
                )}>
                  <p className={cn("text-xs mb-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>Immatriculation</p>
                  <p className={cn("font-bold font-mono", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                    {consultModal.vehicle?.immatriculation}
                  </p>
                </div>
                
                <div className={cn(
                  "p-3 rounded-lg",
                  theme === 'dark' ? 'bg-zinc-800/50' : 'bg-gray-50'
                )}>
                  <p className={cn("text-xs mb-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>Marque / Modèle</p>
                  <p className={cn("font-medium", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                    {consultModal.vehicle?.marque} {consultModal.vehicle?.modele}
                  </p>
                </div>
                
                <div className={cn(
                  "p-3 rounded-lg",
                  theme === 'dark' ? 'bg-zinc-800/50' : 'bg-gray-50'
                )}>
                  <p className={cn("text-xs mb-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>Année</p>
                  <p className={cn("font-medium", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                    {consultModal.vehicle?.annee}
                  </p>
                </div>
                
                <div className={cn(
                  "p-3 rounded-lg",
                  theme === 'dark' ? 'bg-zinc-800/50' : 'bg-gray-50'
                )}>
                  <p className={cn("text-xs mb-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>Kilométrage</p>
                  <p className={cn("font-medium", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                    {consultModal.vehicle?.kilometrage?.toLocaleString() || 0} km
                  </p>
                </div>
                
                <div className={cn(
                  "p-3 rounded-lg",
                  theme === 'dark' ? 'bg-zinc-800/50' : 'bg-gray-50'
                )}>
                  <p className={cn("text-xs mb-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>Carburant</p>
                  <p className={cn("font-medium capitalize", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                    {consultModal.vehicle?.carburant || 'Non renseigné'}
                  </p>
                </div>
                
                <div className={cn(
                  "p-3 rounded-lg",
                  theme === 'dark' ? 'bg-zinc-800/50' : 'bg-gray-50'
                )}>
                  <p className={cn("text-xs mb-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>Statut</p>
                  <div className="flex items-center gap-2">
                    <div className={cn("w-2.5 h-2.5 rounded-full", STATUS_CONFIG[consultModal.vehicle?.statut]?.color || 'bg-gray-400')} />
                    <p className={cn("font-medium", STATUS_CONFIG[consultModal.vehicle?.statut]?.filterColor || 'text-gray-500')}>
                      {STATUS_CONFIG[consultModal.vehicle?.statut]?.label || 'Inconnu'}
                    </p>
                  </div>
                </div>
                
                <div className={cn(
                  "p-3 rounded-lg",
                  theme === 'dark' ? 'bg-zinc-800/50' : 'bg-gray-50'
                )}>
                  <p className={cn("text-xs mb-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>Date Assurance</p>
                  <p className={cn("font-medium", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                    {consultModal.vehicle?.date_assurance ? formatDate(consultModal.vehicle.date_assurance) : 'Non renseignée'}
                  </p>
                </div>
                
                <div className={cn(
                  "p-3 rounded-lg",
                  theme === 'dark' ? 'bg-zinc-800/50' : 'bg-gray-50'
                )}>
                  <p className={cn("text-xs mb-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>Contrôle Technique</p>
                  <p className={cn("font-medium", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                    {consultModal.vehicle?.date_controle_technique ? formatDate(consultModal.vehicle.date_controle_technique) : 'Non renseigné'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Documents Section - View Only */}
            <div>
              <h3 className={cn("text-sm font-semibold mb-3 flex items-center gap-2", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                <FileText size={16} className="text-blue-500" />
                Documents
                <span className={cn("text-xs font-normal", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                  ({(consultModal.vehicle?.documents || []).length} document{(consultModal.vehicle?.documents || []).length !== 1 ? 's' : ''})
                </span>
              </h3>
              
              {(consultModal.vehicle?.documents || []).length === 0 ? (
                <div className={cn(
                  "text-center py-8 rounded-lg border",
                  theme === 'dark' ? 'bg-zinc-800/30 border-white/10 text-zinc-500' : 'bg-gray-50 border-gray-200 text-gray-500'
                )}>
                  <FileText size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucun document disponible</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(consultModal.vehicle?.documents || []).map((doc) => {
                    const category = DOCUMENT_CATEGORIES.find(c => c.id === doc.category);
                    return (
                      <div 
                        key={doc.id}
                        className={cn(
                          "p-3 rounded-lg border",
                          theme === 'dark' ? 'bg-zinc-800/50 border-white/10' : 'bg-gray-50 border-gray-200'
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={cn(
                            "text-sm font-medium",
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          )}>
                            {category?.label || doc.category}
                          </span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handlePreviewDocument(doc)}
                              className={cn(
                                "p-1.5 rounded-lg transition-colors",
                                theme === 'dark' ? 'hover:bg-purple-500/20 text-purple-400' : 'hover:bg-purple-100 text-purple-600'
                              )}
                              title="Prévisualiser"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = `${process.env.REACT_APP_BACKEND_URL}/api/vehicles/${consultModal.vehicle.id}/documents/${doc.id}/download`;
                                link.target = '_blank';
                                link.click();
                              }}
                              className={cn(
                                "p-1.5 rounded-lg transition-colors",
                                theme === 'dark' ? 'hover:bg-blue-500/20 text-blue-400' : 'hover:bg-blue-100 text-blue-600'
                              )}
                              title="Télécharger"
                            >
                              <Download size={16} />
                            </button>
                          </div>
                        </div>
                        <p className={cn("text-xs truncate", theme === 'dark' ? 'text-zinc-400' : 'text-gray-600')}>
                          {doc.filename}
                        </p>
                        <div className={cn("text-[10px] mt-1 flex items-center justify-between", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                          <span>{(doc.size / 1024).toFixed(1)} KB</span>
                          <span>{doc.uploaded_at ? formatUpdateDate(doc.uploaded_at) : ''}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConsultModal({ isOpen: false, vehicle: null })}
              className={theme === 'dark' ? 'border-white/10' : ''}
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
